import type { ReactNode } from 'react'
import { useMemo } from 'react'
import { useIntl } from 'react-intl'
import { rem } from 'polished'
import styled from 'styled-components'

import Group from 'components/utils/Group'
import Heading from 'LuxKit/Typography/Heading'
import BodyText from 'LuxKit/Typography/BodyText'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import CaptionTextBlock from 'LuxKit/TextBlocks/CaptionTextBlock'
import TextLink from 'LuxKit/TextLink'
import NeutralLabel from 'LuxKit/Label/NeutralLabel'
import Divider from 'LuxKit/Divider'
import LineCalendarIcon from 'LuxKit/Icons/line/LineCalendarIcon'
import LineArrowRightIcon from 'LuxKit/Icons/line/LineArrowRightIcon'
import LineCheckCircleIcon from 'LuxKit/Icons/line/LineCheckCircleIcon'
import LineUsersAltIcon from 'LuxKit/Icons/line/LineUsersAltIcon'
import LineUserSquareIcon from 'LuxKit/Icons/line/LineUserSquareIcon'
import ImageCarousel from 'LuxKit/Carousel/ImageCarousel'
import ResponsiveImage from 'components/Common/ResponsiveImage'
import { INTL_DMY_CASUAL_SHORT_FORMAT } from 'constants/dateFormats'
import { intlDateFormatter } from 'lib/datetime/dateUtils'
import { useScreenSizeOnly } from 'hooks/useScreenSize'
import type { DrawerHotelBookedRoom } from 'tripPlanner/utils/types'

const BookedRoomCard = styled.div`
  border: 1px solid ${(props) => props.theme.palette.neutral.default.five};
  border-radius: ${(props) => props.theme.borderRadius.S};
  padding: ${rem(20)};

  &.compact {
    padding: ${rem(16)};
  }
`

const DesktopImageColumn = styled.div`
  width: ${rem(237)};
  height: ${rem(137)};
  flex-shrink: 0;
  border-radius: ${(props) => props.theme.borderRadius.S};
  overflow: hidden;
`

const MobileImageFrame = styled.div`
  width: 100%;
  height: ${rem(166)};
  min-width: 0;
  border-radius: ${(props) => props.theme.borderRadius.S};
  overflow: hidden;
`

const TitleColumn = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1 1 0;
  gap: ${rem(2)};
  min-width: 0;
`

const GuestColumn = styled.div`
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  gap: ${rem(4)};
`

const DesktopDateBox = styled.div`
  border: 1px solid ${(props) => props.theme.palette.neutral.default.five};
  border-radius: ${(props) => props.theme.borderRadius.S};
  padding: ${rem(16)};
`

const BookingInfoBox = styled.div`
  border: 1px solid ${(props) => props.theme.palette.neutral.default.five};
  border-radius: ${(props) => props.theme.borderRadius.S};
  width: 100%;
  min-width: 0;
  padding-block: ${rem(12)};
`

const BookingInfoSection = styled.div`
  padding-inline: ${rem(12)};
`

interface BookedRoomLabels {
  roomLabel: string
  checkInHeader: string
  checkOutHeader: string
  checkInLabel: string
  checkOutLabel: string
  viewAllDetailsLabel: string
  freeCancellationLabel: string
  bookingDetailsUrl: string
}

function useBookedRoomLabels(room: DrawerHotelBookedRoom, index: number, totalRooms: number): BookedRoomLabels {
  const intl = useIntl()

  const roomLabel = totalRooms === 1
    ? intl.formatMessage({
        id: 'mxARc7OBIezIoQHOptV5',
        defaultMessage: 'Your Room',
        description: 'Label for the single room in hotel booking summary',
      })
    : intl.formatMessage(
        {
          id: 'Oqb9kyhd11EEaEGE9nQe',
          defaultMessage: 'Room {roomNumber}',
          description: 'Label for a numbered room in hotel booking summary with multiple rooms',
        },
        { roomNumber: index + 1 },
      )

  return {
    roomLabel,
    checkInHeader: intl.formatMessage({
      id: 'FRU2pokXnES2bxnLJkdy',
      defaultMessage: 'Check-in',
      description: 'Label above the check-in date in a booked room card',
    }),
    checkOutHeader: intl.formatMessage({
      id: 'fJO0_3AVTgQCU7_gg4gU',
      defaultMessage: 'Check-out',
      description: 'Label above the check-out date in a booked room card',
    }),
    checkInLabel: intlDateFormatter(room.checkIn, INTL_DMY_CASUAL_SHORT_FORMAT, intl.locale),
    checkOutLabel: intlDateFormatter(room.checkOut, INTL_DMY_CASUAL_SHORT_FORMAT, intl.locale),
    viewAllDetailsLabel: intl.formatMessage({
      id: 'pKtkMInfl7gquCBZNrVf',
      defaultMessage: 'View all details',
      description: 'Link text to view full booking details on the my escapes page',
    }),
    freeCancellationLabel: intl.formatMessage({
      id: 'hFllTxt192K8UL8t0V9o',
      defaultMessage: 'Free cancellation',
      description: 'Heading for free cancellation status on a booked hotel room card in trip planner',
    }),
    bookingDetailsUrl: `/account/my-escapes/order/${room.orderId}`,
  }
}

function useBookedRoomImage(room: DrawerHotelBookedRoom): ReactNode {
  const carouselImages = useMemo<Array<App.Image>>(
    () => room.roomImageIds.map((id) => ({ id })),
    [room.roomImageIds],
  )
  const singleImageId = carouselImages.length === 1 ? carouselImages[0].id : undefined

  if (singleImageId) {
    return <ResponsiveImage id={singleImageId} alt={room.roomTypeName} fit="center" />
  }
  if (carouselImages.length > 1) {
    return <ImageCarousel images={carouselImages} arrowBehaviour="interaction-visible" />
  }
  return null
}

function BookingIdLabel({ bookingNumber }: { bookingNumber: string }) {
  const intl = useIntl()

  return (
    <BodyTextBlock variant="medium">
      {intl.formatMessage(
        {
          id: '96MtMoe3WeX_KXHYg5XT',
          defaultMessage: '<b>Booking ID:</b> {bookingNumber}',
          description: 'Label for hotel booking ID number on the hotel or room booking page',
        },
        {
          b: (chunks: ReactNode) => <b>{chunks}</b>,
          bookingNumber,
        },
      )}
    </BodyTextBlock>
  )
}

function RoomLabelRow({
  roomLabel,
  bookingNumber,
}: {
  roomLabel: string
  bookingNumber?: string
}) {
  return (
    <Group direction="horizontal" horizontalAlign="space-between" verticalAlign="center">
      <NeutralLabel>{roomLabel}</NeutralLabel>
      {bookingNumber && <BookingIdLabel bookingNumber={bookingNumber} />}
    </Group>
  )
}

function CheckInOutDates({
  checkInHeader,
  checkOutHeader,
  checkInLabel,
  checkOutLabel,
}: Pick<BookedRoomLabels, 'checkInHeader' | 'checkOutHeader' | 'checkInLabel' | 'checkOutLabel'>) {
  return (
    <Group direction="horizontal" gap={12} verticalAlign="center">
      <LineCalendarIcon size="L" colour="normal-contrast" />
      <Group direction="vertical">
        <BodyText variant="small" colour="light-foreground-alt">{checkInHeader}</BodyText>
        <BodyText variant="medium" weight="bold">{checkInLabel}</BodyText>
      </Group>
      <LineArrowRightIcon colour="light-foreground-alt" />
      <Group direction="vertical">
        <BodyText variant="small" colour="light-foreground-alt">{checkOutHeader}</BodyText>
        <BodyText variant="medium" weight="bold">{checkOutLabel}</BodyText>
      </Group>
    </Group>
  )
}

function MobileGuestDetails({
  guestSummary,
  leadGuestName,
}: {
  guestSummary?: string
  leadGuestName?: string
}) {
  if (!guestSummary && !leadGuestName) return null

  return (
    <GuestColumn data-testid="booked-room-guests">
      {guestSummary && (
        <CaptionTextBlock variant="large" weight="bold" startIcon={<LineUsersAltIcon />}>
          {guestSummary}
        </CaptionTextBlock>
      )}
      {leadGuestName && (
        <CaptionTextBlock variant="large" weight="bold" startIcon={<LineUserSquareIcon />}>
          {leadGuestName}
        </CaptionTextBlock>
      )}
    </GuestColumn>
  )
}

function MobileFreeCancellation({
  label,
  detail,
}: {
  label: string
  detail: string
}) {
  return (
    <BookingInfoSection data-testid="booked-room-cancellation">
      <Group direction="horizontal" gap={12} verticalAlign="start">
        <LineCheckCircleIcon size="M" colour="highlight-secondary" />
        <Group direction="vertical" gap={4}>
          <BodyText variant="medium" weight="bold" colour="highlight-secondary">
            {label}
          </BodyText>
          <BodyText variant="medium">{detail}</BodyText>
        </Group>
      </Group>
    </BookingInfoSection>
  )
}

interface BookedRoomCardItemProps {
  room: DrawerHotelBookedRoom
  index: number
  totalRooms: number
}

function BookedRoomCardItem({ room, index, totalRooms }: BookedRoomCardItemProps) {
  const isMobile = useScreenSizeOnly('mobile')
  const labels = useBookedRoomLabels(room, index, totalRooms)
  const roomImage = useBookedRoomImage(room)
  const hasDates = Boolean(labels.checkInLabel || labels.checkOutLabel)
  const freeCancellationDetail = room.freeCancellationDetail

  if (isMobile) {
    return (
      <BookedRoomCard className="compact" data-testid="booked-room-card" data-layout="mobile">
        <Group direction="vertical" gap={12}>
          <RoomLabelRow roomLabel={labels.roomLabel} bookingNumber={room.bookingNumber} />

          <Group direction="vertical" gap={16}>
            <Group direction="vertical" gap={12}>
              <Group direction="horizontal" gap={16} horizontalAlign="space-between" verticalAlign="start">
                <TitleColumn>
                  <Heading variant="heading6">{room.roomTypeName}</Heading>
                  {room.packageName && (
                    <BodyText variant="medium" colour="light-foreground-alt">
                      {room.packageName}
                    </BodyText>
                  )}
                </TitleColumn>
                <MobileGuestDetails
                  guestSummary={room.guestSummary}
                  leadGuestName={room.leadGuestName}
                />
              </Group>

              {roomImage && (
                <MobileImageFrame data-testid="booked-room-image">
                  {roomImage}
                </MobileImageFrame>
              )}
            </Group>

            {(freeCancellationDetail || hasDates) && (
              <BookingInfoBox data-testid="booked-room-booking-info">
                {freeCancellationDetail && (
                  <>
                    <MobileFreeCancellation
                      label={labels.freeCancellationLabel}
                      detail={freeCancellationDetail}
                    />
                    {hasDates && <Divider kind="primary" />}
                  </>
                )}
                {hasDates && (
                  <BookingInfoSection>
                    <CheckInOutDates
                      checkInHeader={labels.checkInHeader}
                      checkOutHeader={labels.checkOutHeader}
                      checkInLabel={labels.checkInLabel}
                      checkOutLabel={labels.checkOutLabel}
                    />
                  </BookingInfoSection>
                )}
              </BookingInfoBox>
            )}

            <TextLink
              size="medium"
              href={labels.bookingDetailsUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              {labels.viewAllDetailsLabel}
            </TextLink>
          </Group>
        </Group>
      </BookedRoomCard>
    )
  }

  // Desktop path intentionally preserves the existing side-by-side layout.
  return (
    <BookedRoomCard data-testid="booked-room-card" data-layout="desktop">
      <Group direction="vertical" gap={16}>
        <RoomLabelRow roomLabel={labels.roomLabel} bookingNumber={room.bookingNumber} />

        <Group direction="vertical" gap={20}>
          <Group direction="horizontal" horizontalAlign="space-between" verticalAlign="start">
            <Group direction="vertical" gap={4}>
              <Heading variant="heading6">{room.roomTypeName}</Heading>
              {room.packageName && (
                <BodyText variant="medium" colour="light-foreground-alt">
                  {room.packageName}
                </BodyText>
              )}
            </Group>
            {roomImage && (
              <DesktopImageColumn data-testid="booked-room-image">
                {roomImage}
              </DesktopImageColumn>
            )}
          </Group>

          {hasDates && (
            <DesktopDateBox>
              <CheckInOutDates
                checkInHeader={labels.checkInHeader}
                checkOutHeader={labels.checkOutHeader}
                checkInLabel={labels.checkInLabel}
                checkOutLabel={labels.checkOutLabel}
              />
            </DesktopDateBox>
          )}

          <TextLink
            size="medium"
            href={labels.bookingDetailsUrl}
            target="_blank"
            rel="noopener noreferrer"
          >
            {labels.viewAllDetailsLabel}
          </TextLink>
        </Group>
      </Group>
    </BookedRoomCard>
  )
}

interface Props {
  bookedRooms: Array<DrawerHotelBookedRoom>
}

function HotelDetailBookedRoom({ bookedRooms }: Props) {
  const intl = useIntl()

  return (
    <Group direction="vertical" gap={12}>
      <Heading variant="heading5">
        {intl.formatMessage({
          id: 'jiBFd9qhjviP_yYV2tN3',
          defaultMessage: 'Room details',
          description: 'Heading for the booked room section in the hotel detail drawer',
        })}
      </Heading>
      {bookedRooms.map((room, index) => (
        <BookedRoomCardItem
          key={room.bookingNumber ?? `${room.orderId}-${index}`}
          room={room}
          index={index}
          totalRooms={bookedRooms.length}
        />
      ))}
    </Group>
  )
}

export default HotelDetailBookedRoom
